import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import React from 'react';
import { QuizExercise } from '../../mockLessonContent';
import { BLUE, ERROR_TEXT, GREEN, INTER, NAVY, POPPINS, RED, RING_TRACK, SLATE, SUCCESS_TEXT, SURFACE_SOFT } from '../../studentStyles';
import AudioPlayButton from '../AudioPlayButton';
import ExerciseLabel from './ExerciseLabel';
import RecordAnswerPanel from './RecordAnswerPanel';

interface QuizExerciseCardProps {
    exercise: QuizExercise;
    onReady: () => void;
}

const labelByMode: Record<QuizExercise['mode'], string> = {
    text: 'QUICK CHECK',
    picture: 'PICTURE CHECK',
    audio: 'LISTENING CHECK',
};

const optionLetters = ['A', 'B', 'C', 'D', 'E', 'F'];

// "Quiz: Select One" (text/picture), "Quiz: Select One Audio" (the choices are
// clips, revealed once answered) and "Quiz: Select One, then Speak" (speakAfter).
export default function QuizExerciseCard({ exercise, onReady }: QuizExerciseCardProps) {
    const [wrongPicks, setWrongPicks] = React.useState<number[]>([]);
    const [solved, setSolved] = React.useState(false);

    const handlePick = (index: number) => {
        if (index === exercise.correctIndex) {
            setSolved(true);
            if (!exercise.speakAfter) {
                onReady();
            }
        } else {
            setWrongPicks(prev => (prev.includes(index) ? prev : [...prev, index]));
        }
    };

    const isAudio = exercise.mode === 'audio';

    return (
        <Box>
            <ExerciseLabel>{labelByMode[exercise.mode]}</ExerciseLabel>
            {exercise.imageEmoji && (
                <Box sx={{
                    width: 112, height: 112, borderRadius: '24px', mx: 'auto', mb: 2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: SURFACE_SOFT,
                }}>
                    <Typography sx={{ fontSize: 56, lineHeight: 1 }}>{exercise.imageEmoji}</Typography>
                </Box>
            )}
            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY, lineHeight: 1.35, textAlign: exercise.imageEmoji ? 'center' : 'left' }}>
                {exercise.prompt}
            </Typography>
            <Stack spacing={1.25} sx={{ mt: 2.5 }}>
                {exercise.options.map((option, index) => {
                    const isWrong = wrongPicks.includes(index);
                    const isCorrect = solved && index === exercise.correctIndex;
                    // Audio choices stay unlabelled until the round is over, so the
                    // student has to go by ear.
                    const showText = !isAudio || solved || isWrong;
                    return (
                        <Box
                            key={option.hanzi}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                borderRadius: '16px',
                                backgroundColor: isCorrect ? `${GREEN}14` : isWrong ? `${RED}14` : '#fff',
                                border: `2px solid ${isCorrect ? GREEN : isWrong ? RED : 'transparent'}`,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                opacity: solved && !isCorrect ? 0.5 : 1,
                                transition: 'background-color 150ms ease, border-color 150ms ease, opacity 150ms ease',
                                pr: isAudio ? 1.5 : 0,
                            }}
                        >
                            <ButtonBase
                                onClick={() => handlePick(index)}
                                disabled={solved || isWrong}
                                sx={{ flex: 1, minWidth: 0, p: 2, borderRadius: '16px', display: 'flex', alignItems: 'center', gap: 1.5, textAlign: 'left' }}
                            >
                                {isAudio && (
                                    <Box sx={{
                                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        backgroundColor: `${BLUE}14`,
                                    }}>
                                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 13, color: BLUE }}>
                                            {optionLetters[index]}
                                        </Typography>
                                    </Box>
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {showText ? (
                                        <>
                                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>
                                                {option.hanzi}
                                            </Typography>
                                            <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>
                                                {option.pinyin}
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography sx={{ fontFamily: INTER, fontSize: 14, color: SLATE }}>
                                            Tap to choose this one
                                        </Typography>
                                    )}
                                </Box>
                                {isCorrect && <CheckCircleRoundedIcon sx={{ color: GREEN }} />}
                                {isWrong && <CancelRoundedIcon sx={{ color: RED }} />}
                            </ButtonBase>
                            {isAudio && <AudioPlayButton ariaLabel={`Play option ${optionLetters[index]}`} size={40} />}
                        </Box>
                    );
                })}
            </Stack>
            {(solved || wrongPicks.length > 0) && (
                <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 13, color: solved ? SUCCESS_TEXT : ERROR_TEXT, mt: 1.5 }}>
                    {solved ? "对了! That's it." : 'Not quite — try another one'}
                </Typography>
            )}

            {/* "Quiz: Select One, then Speak": the answer still has to be said out loud. */}
            {exercise.speakAfter && solved && (
                <Box sx={{ mt: 2.5, pt: 2.5, borderTop: `1px solid ${RING_TRACK}` }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY, lineHeight: 1.4 }}>
                        {exercise.speakAfter.instruction}
                    </Typography>
                    <Box sx={{ mt: 1.5, p: 2, borderRadius: '16px', backgroundColor: SURFACE_SOFT, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 500, fontSize: 18, color: NAVY }}>
                                {exercise.options[exercise.correctIndex].hanzi}
                            </Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>
                                {exercise.options[exercise.correctIndex].pinyin}
                            </Typography>
                        </Box>
                        <AudioPlayButton ariaLabel="Play the model answer" size={40} />
                    </Box>
                    <RecordAnswerPanel grades={exercise.speakAfter.grades} onScored={onReady} />
                </Box>
            )}
        </Box>
    );
}
